import { useEffect, useMemo, useRef, useState } from 'react'

import {
  CLINICAL_SNIPPETS,
  groupSnippetsByCategory,
  searchSnippets,
} from '../data/clinicalSnippets.js'

/**
 * SnippetPicker — drop next to any long-form clinical text field.
 *
 * Renders a small "💡 Snippets" button. On click, opens a popover with
 * a search box at the top + categorised list of matching snippets.
 * Clicking a snippet calls `onInsert(text)` so the parent can decide
 * how to splice it into the textarea (append, replace, or insert at
 * cursor — typical pattern is `value + (value ? '\n' : '') + text`).
 *
 * Design intent: zero-keystroke UX for the common case (open, scan,
 * pick) but search-as-you-type for power users who know the snippet
 * by name. Keyboard: Esc closes, ↑/↓ navigates results, Enter inserts
 * the highlighted snippet.
 *
 * Props:
 *   onInsert(text)  required — called with the snippet body string
 *   buttonClassName  optional — tweak the button styling
 *   buttonLabel      optional — defaults to "💡 Snippets"
 *   filter           optional — { category: 'Antibiotics' } to scope
 *                    the picker to one section (e.g. only treatment
 *                    snippets next to the Empirical treatment field).
 */
export default function SnippetPicker({
  onInsert,
  buttonClassName = '',
  buttonLabel = '💡 Snippets',
  filter = null,
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const popRef = useRef(null)
  const buttonRef = useRef(null)

  // Filter pool: optionally scope to one category, then by query.
  const pool = useMemo(() => {
    let p = CLINICAL_SNIPPETS
    if (filter?.category) {
      p = p.filter((s) => s.category === filter.category)
    }
    return p
  }, [filter?.category])

  const matches = useMemo(() => searchSnippets(q, pool), [q, pool])
  const grouped = useMemo(() => groupSnippetsByCategory(matches), [matches])

  // Click-outside dismiss + Esc.
  useEffect(() => {
    if (!open) return
    function onDocClick(e) {
      if (
        popRef.current && !popRef.current.contains(e.target)
        && buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Reset highlight when matches change.
  useEffect(() => {
    setActiveIdx(0)
  }, [q, pool])

  function pick(snippet) {
    onInsert(snippet.body)
    setOpen(false)
    setQ('')
  }

  function onKeyDownSearch(e) {
    if (matches.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      pick(matches[activeIdx])
    }
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        className={
          'text-[11px] font-semibold border border-wantok-200 bg-wantok-50/60 text-wantok-800 ' +
          'hover:bg-wantok-100 rounded-md px-2 py-1 ' + buttonClassName
        }
        title="Insert a clinical text snippet"
      >
        {buttonLabel}
      </button>

      {open && (
        <div
          ref={popRef}
          className={
            'absolute z-50 right-0 mt-1 w-[420px] max-w-[90vw] ' +
            'bg-white border border-slate-200 rounded-lg shadow-lg ' +
            'overflow-hidden'
          }
        >
          <div className="p-2 border-b border-slate-100 bg-slate-50/60">
            <input
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDownSearch}
              placeholder="Type to search snippets — e.g. 'triple', 'malaria', 'htn'…"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:border-wantok-600 focus:outline-none focus:ring-1 focus:ring-wantok-600"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              {matches.length} snippet{matches.length === 1 ? '' : 's'}
              {filter?.category ? ` in ${filter.category}` : ''} ·
              {' '}↑/↓ navigate · Enter insert · Esc close
            </div>
          </div>

          <div className="max-h-[55vh] overflow-y-auto p-1">
            {matches.length === 0 ? (
              <div className="text-xs text-slate-400 italic p-3 text-center">
                No snippets match "{q}".
              </div>
            ) : (
              grouped.map(({ category, items }) => (
                <div key={category} className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 px-2 pt-2 pb-1">
                    {category}
                  </div>
                  {items.map((s) => {
                    const flatIdx = matches.indexOf(s)
                    const isActive = flatIdx === activeIdx
                    return (
                      <button
                        key={s.trigger}
                        type="button"
                        onClick={() => pick(s)}
                        onMouseEnter={() => setActiveIdx(flatIdx)}
                        className={
                          'w-full text-left rounded px-2 py-1.5 transition-colors ' +
                          (isActive
                            ? 'bg-wantok-50 text-wantok-900'
                            : 'hover:bg-slate-50 text-slate-800')
                        }
                      >
                        <div className="text-xs font-semibold">{s.trigger}</div>
                        <div className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-2">
                          {s.body}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
